import { ArrowLeftOutlined, ThunderboltFilled } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { type Dispatch } from "@reduxjs/toolkit";
import type { FormProps } from "antd";
import {
	Alert,
	Button,
	Card,
	ConfigProvider,
	Divider,
	Flex,
	Form,
	Input,
	Layout,
	Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { routeNames } from "./common/routes";
import { tokenActions } from "../features/token/tokenSlice";
import styles from "./LoginPage.module.css";
import { silentClient } from "./api-client";
import type { RootState } from "./store";
import { loginTheme } from "./theme";

type CredentialsFieldType = {
	username: string;
	password: string;
};

type TotpFieldType = {
	totp: string;
};

type AppProps = {
	dispatch: Dispatch;
	intl: IntlShape;
};

const LoginPage: React.FC<AppProps> = ({ dispatch, intl }) => {
	const [step, setStep] = useState<1 | 2>(1);
	const [challengeToken, setChallengeToken] = useState<string>("");
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);
	const totpInputRef = useRef<ReturnType<typeof Input> | null>(null);

	const handleCredentialsSubmit: FormProps<CredentialsFieldType>["onFinish"] =
		async (values) => {
			setIsLoading(true);
			setErrorMessageKey(null);
			try {
				const response =
					await silentClient.apiV1ManagementAuthCheckCredentialsCreate({
						username: values.username,
						password: values.password,
					});
				if (response?.data?.challenge) {
					setChallengeToken(response.data.challenge);
					setStep(2);
				} else {
					setErrorMessageKey("login_error_invalid_credentials");
				}
			} catch (error: unknown) {
				const axiosError = error as { response?: unknown; code?: string };
				if (axiosError?.code === "ERR_NETWORK") {
					setErrorMessageKey("login_error_network");
				} else {
					setErrorMessageKey("login_error_invalid_credentials");
				}
			} finally {
				setIsLoading(false);
			}
		};

	const handleTotpSubmit: FormProps<TotpFieldType>["onFinish"] = async (
		values,
	) => {
		setIsLoading(true);
		setErrorMessageKey(null);
		try {
			const response = await silentClient.apiV1ManagementAuthLoginCreate({
				challenge: challengeToken,
				totp: values.totp,
			});
			if (response?.data?.access && response?.data?.refresh) {
				localStorage.setItem("access", response.data.access);
				localStorage.setItem("refresh", response.data.refresh);
				dispatch(tokenActions.addToken(response.data));
				dispatch(routerActions.navigateTo(routeNames.TICKET_LIST));
			} else {
				setErrorMessageKey("login_error_invalid_totp");
			}
		} catch (error: unknown) {
			const axiosError = error as {
				response?: { status?: number };
				code?: string;
			};
			if (axiosError?.code === "ERR_NETWORK") {
				setErrorMessageKey("login_error_network");
			} else if (axiosError?.response?.status === 401) {
				setChallengeToken("");
				setStep(1);
				setErrorMessageKey("login_error_session_expired");
			} else {
				setErrorMessageKey("login_error_invalid_totp");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackToStep1 = () => {
		setChallengeToken("");
		setStep(1);
		setErrorMessageKey(null);
	};

	return (
		<ConfigProvider theme={loginTheme}>
			<Layout>
				<Content>
					<Flex
						vertical
						className={styles.pageWrapper}
						align="center"
						justify="center"
					>
						<Card className={styles.card}>
							<Flex align="center" gap={12} className={styles.brand}>
								<Flex
									align="center"
									justify="center"
									className={styles.iconWrap}
								>
									<ThunderboltFilled className={styles.brandIcon} />
								</Flex>
								<Flex vertical>
									<Typography.Text className={styles.brandName}>
										DZ Projects
									</Typography.Text>
									<Typography.Text className={styles.brandTag}>
										Wind Asset Management
									</Typography.Text>
								</Flex>
							</Flex>

							<Divider className={styles.divider} />

							{step === 1 ? (
								<>
									<Typography.Title level={4} className={styles.heading}>
										{intl.formatMessage({ id: "login_header" })}
									</Typography.Title>
									<Typography.Text className={styles.subHeading}>
										{intl.formatMessage({ id: "login_subheader" })}
									</Typography.Text>

									{errorMessageKey && (
										<Alert
											type="error"
											message={intl.formatMessage({ id: errorMessageKey })}
											className={styles.errorAlert}
											showIcon
										/>
									)}

									<Form
										name="credentials"
										layout="vertical"
										onFinish={handleCredentialsSubmit}
										autoComplete="off"
										className={styles.form}
									>
										<Form.Item<CredentialsFieldType>
											label={
												<Typography.Text className={styles.label}>
													{intl.formatMessage({ id: "login_field_username" })}
												</Typography.Text>
											}
											name="username"
											rules={[
												{
													required: true,
													message: intl.formatMessage({
														id: "login_input_username_missing",
													}),
												},
											]}
										>
											<Input
												size="large"
												placeholder={intl.formatMessage({
													id: "login_field_username",
												})}
											/>
										</Form.Item>

										<Form.Item<CredentialsFieldType>
											label={
												<Typography.Text className={styles.label}>
													{intl.formatMessage({ id: "login_field_password" })}
												</Typography.Text>
											}
											name="password"
											rules={[
												{
													required: true,
													message: intl.formatMessage({
														id: "login_input_password_missing",
													}),
												},
											]}
										>
											<Input.Password size="large" placeholder="••••••••" />
										</Form.Item>

										<Flex
											justify="flex-end"
											className={styles.forgotPasswordWrapper}
										>
											<Typography.Link
												onClick={() =>
													dispatch(
														routerActions.navigateTo(
															routeNames.USER_FORGOT_PASSWORD,
														),
													)
												}
											>
												{intl.formatMessage({ id: "forgot_password_link" })}
											</Typography.Link>
										</Flex>

										<Form.Item label={null} className={styles.submitFormItem}>
											<Button
												type="primary"
												htmlType="submit"
												size="large"
												block
												loading={isLoading}
												disabled={isLoading}
												style={{ fontWeight: 600 }}
											>
												{intl.formatMessage({ id: "login_button_submit" })}
											</Button>
										</Form.Item>
									</Form>
								</>
							) : (
								<>
									<Typography.Title level={4} className={styles.heading}>
										{intl.formatMessage({ id: "login_step2_header" })}
									</Typography.Title>
									<Typography.Text className={styles.subHeading}>
										{intl.formatMessage({ id: "login_step2_subheader" })}
									</Typography.Text>

									{errorMessageKey && (
										<Alert
											type="error"
											message={intl.formatMessage({ id: errorMessageKey })}
											className={styles.errorAlert}
											showIcon
										/>
									)}

									<Form
										name="totp"
										layout="vertical"
										onFinish={handleTotpSubmit}
										autoComplete="off"
										className={styles.form}
									>
										<Form.Item<TotpFieldType>
											label={
												<Typography.Text className={styles.label}>
													{intl.formatMessage({ id: "login_field_totp" })}
												</Typography.Text>
											}
											name="totp"
											rules={[
												{
													required: true,
													message: intl.formatMessage({
														id: "login_field_totp_missing",
													}),
												},
											]}
										>
											<Input
												size="large"
												placeholder={intl.formatMessage({
													id: "login_field_totp_placeholder",
												})}
												maxLength={6}
												autoFocus
												ref={totpInputRef}
											/>
										</Form.Item>

										<Form.Item label={null} className={styles.submitFormItem}>
											<Button
												type="primary"
												htmlType="submit"
												size="large"
												block
												loading={isLoading}
												disabled={isLoading}
												style={{ fontWeight: 600 }}
											>
												{intl.formatMessage({ id: "login_button_verify" })}
											</Button>
										</Form.Item>
									</Form>

									<Flex justify="center" className={styles.backLinkWrapper}>
										<Typography.Link onClick={handleBackToStep1}>
											<ArrowLeftOutlined />{" "}
											{intl.formatMessage({ id: "login_back" })}
										</Typography.Link>
									</Flex>
								</>
							)}
						</Card>
					</Flex>
				</Content>
			</Layout>
		</ConfigProvider>
	);
};

export const ConnectedLoginPage = connect((state: RootState) => ({
	router: state.router.route,
	intl: state.intl.datetimeIntl,
}))(LoginPage);

export default ConnectedLoginPage;
